import { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "motion/react";

export default function ProductGallery({ gallery = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Safety check
  if (!gallery || !Array.isArray(gallery) || gallery.length === 0) {
    return (
      <div className="aspect-[4/4.2] w-full bg-neutral-100 flex items-center justify-center rounded-[1.75rem]">
        <p className="text-muted-foreground">No images available</p>
      </div>
    );
  }

  const activeMedia = gallery[activeIndex] || gallery[0];

  const goTo = (direction) => {
    setActiveIndex((current) => {
      if (direction === "next") return (current + 1) % gallery.length;
      return current === 0 ? gallery.length - 1 : current - 1;
    });
  };

  return (
    <section className="space-y-4">
      <div className="relative overflow-hidden rounded-none  sm:rounded-[1.75rem]">
        <motion.img
          key={activeMedia.src}
          src={activeMedia.src}
          alt={activeMedia.alt || "Product image"}
          initial={{ opacity: 0, scale: 0.985 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
          className="aspect-[4/4.2] w-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://via.placeholder.com/800x800?text=Image+Not+Found";
          }}
        />

        {gallery.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => goTo("prev")}
              className="absolute left-4 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-black shadow-lg"
            >
              <ArrowLeft className="size-4" />
            </button>

            <button
              type="button"
              onClick={() => goTo("next")}
              className="absolute right-4 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-black shadow-lg"
            >
              <ArrowRight className="size-4" />
            </button>
          </>
        )}

        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
          {gallery.map((item, index) => (
            <button
              key={item.src || index}
              onClick={() => setActiveIndex(index)}
              className={`h-1.5 rounded-full transition-all ${
                activeIndex === index ? "w-8 bg-red-600" : "w-2 bg-white/70"
              }`}
              aria-label={`View image ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="scrollbar-hide flex gap-3 overflow-x-auto pb-1">
        {gallery.map((item, index) => {
          const isActive = activeIndex === index;
          return (
            <button
              key={`${item.src}-${index}`}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-neutral-100 transition sm:h-20 sm:w-20 ${
                isActive ? "border-black ring-2 ring-black" : "border-border"
              }`}
            >
              <img
                src={item.src}
                alt={item.alt || `Product image ${index + 1}`}
                className="size-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/100x100?text=No+Image";
                }}
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}
